import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { roles } from '../utils/mockData';
import { Plus, Edit, Trash2, X, AlertCircle, Loader, CircleMinus, CirclePlus} from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";

// Servicios
import { BancoService } from '../services/bancoService';
import { MunicipioService } from '../services/municipioService';
import { DepartamentoService } from '../services/departamentoService';

// Tipos de Response
import { EmpleadoResponse } from '../types/empleado';

// Modales
import CrearBanco from '../modals/CrearBanco';
import CrearDepartamento from '../modals/CrearDepartamento';
import CrearMunicipio from '../modals/CrearMunicipio';
import { BancoResponse, DepartamentoResponse, MunicipioResponse } from '../types/puesto';

import LogoBlanco from '../components/img/LogoBlanco.png';

export function Otros() {
  const [empleados, setEmpleados] = useState<EmpleadoResponse[]>([]);

  const [bancos, setBancos] = useState<BancoResponse[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioResponse[]>([]);
  const [departamentos, setDepartamentos] = useState<DepartamentoResponse[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostrarFormularioBanco, setMostrarFormularioBanco] = useState(false);
  const [mostrarFormularioDepartamento, setMostrarFormularioDepartamento] = useState(false);
  const [mostrarFormularioMunicipio, setMostrarFormularioMunicipio] = useState(false);
  const [bancoEditando, setBancoEditando] = useState<number>(0);
  const [departamentoEditando, setDepartamentoEditando] = useState<number>(0);
  const [municipioEditando, setMunicipioEditando] = useState<number>(0);
  const [filtroActivoBanco, setFiltroActivoBanco] = useState(true);
  const [filtroActivoDepartamento, setFiltroActivoDepartamento] = useState(true);
  const [filtroActivoMunicipio, setFiltroActivoMunicipio] = useState(true);
  const { addToast } = useToast();

  // Cargar cosas al montar el componente y cada vez que cambie el filtro
  useEffect(() => {
    cargarBancos();
  }, [filtroActivoBanco]);

  useEffect(() => {
    cargarDepartamentos();
  }, [filtroActivoDepartamento]);

  useEffect(() => {
    cargarMunicipios();
  }, [filtroActivoMunicipio]);

  const cargarDepartamentos = async () => {
    try {
      setIsLoading(true);
      const res = await DepartamentoService.getAll(filtroActivoDepartamento);
      setDepartamentos(res.data);
    } catch (err) {
      setError("Error al cargar departamentos");
    } finally {
      setIsLoading(false);
    }
  };

  const cargarMunicipios = async () => {
    try {
      setIsLoading(true);
      const res = await MunicipioService.getAll(filtroActivoMunicipio);
      setMunicipios(res.data);
    } catch (err) {
      setError("Error al cargar municipios");
    } finally {
      setIsLoading(false);
    }
  };

  const cargarBancos = async () => {
    try {
      setIsLoading(true);
      const res = await BancoService.getAll(filtroActivoBanco);
      setBancos(res.data);
    } catch (err) {
      setError("Error al cargar bancos");
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarEstadoBanco = async (id: number, activo: boolean) => {
    try {
      setIsLoading(true);
      if (activo) {
        await BancoService.desactivar(id);
        addToast("Banco desactivado exitosamente");
      } else {
        await BancoService.activar(id);
        addToast("Banco activado exitosamente");
      }

      await cargarBancos();
    } catch (err) {
      setError("Error al cambiar estado del banco");
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarEstadoDepartamento = async (id: number, activo: boolean) => {
    try {
      setIsLoading(true);
      if (activo) {
        await DepartamentoService.desactivar(id);
        addToast("Departamento desactivado exitosamente");
      } else {
        await DepartamentoService.activar(id);
        addToast("Departamento activado exitosamente");
      }

      await cargarDepartamentos();
    } catch (err) {
      setError("Error al cambiar estado del Departamento");
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarEstadoMunicipio = async (id: number, activo: boolean) => {
    try {
      setIsLoading(true);
      if (activo) {
        await MunicipioService.desactivar(id);
        addToast("Municipio desactivado exitosamente");
      } else {
        await MunicipioService.activar(id);
        addToast("Municipio activado exitosamente");
      }

      await cargarMunicipios();
    } catch (err) {
      setError("Error al cambiar estado del Municipio");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-6 md:grid-cols-6 gap-4 p-6 text-white bg-primary border border-primary rounded-t-lg items-center">
        {/* Logo */}
        <div className="col-span-2 md:col-span-1 flex justify-center md:justify-start">
            <img 
            src={LogoBlanco} 
            alt="Logo AGEMI" 
            className="w-20 md:w-40 h-auto object-contain"
            />
        </div>
        <div className="col-span-4 md:col-span-5 flex flex-col text-sm md:text-base">
          <h1 className="text-3xl font-bold">Bancos</h1>
        <p className="text-base text-white mt-1 md:mt-2">
          Gestión de los bancos del sistema
        </p>
        </div>
      </div>

      <Card>
      <CardHeader>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <Select
              value={filtroActivoBanco ? '1' : '0'}
              onChange={(e) => setFiltroActivoBanco(e.target.value === '1')}
              options={[
                { value: '1', label: 'Bancos Activos' },
                { value: '0', label: 'Bancos Inactivos' },
              ]}
              className="w-full md:w-auto bg-white text-primary"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-end mb-4 gap-2">
            {!mostrarFormularioBanco && (
              <Button onClick={() => setMostrarFormularioBanco(true)} variant="accent" className="w-full md:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Banco
              </Button>
            )}
          </div>
          {error && (
            <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error al cargar bancos</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary mr-2" />
              <p>Cargando bancos...</p>
            </div>
          ) : bancos.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay bancos para mostrar</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px] border-gray-400 mb-10">
                <table className="min-w-full text-left">
                  <thead className="bg-primary text-white sticky top-0">
                    <tr>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Nombre</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bancos.map((banco) => (
                      <tr key={banco.idBanco} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="px-3 md:px-4 py-2 md:py-3 font-medium text-xs md:text-sm">
                          <div className="truncate">{banco.banco}</div>
                        </td>
                        <td className="px-3 md:px-4 py-2 md:py-3">
                          <div className="flex justify-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="accent"
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                setBancoEditando(Number(banco.idBanco));
                                setMostrarFormularioBanco(true);
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant={filtroActivoBanco ? "destructive" : "primary"}
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                cambiarEstadoBanco(Number(banco.idBanco), filtroActivoBanco);
                              }}
                            >
                              {filtroActivoBanco ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-6 md:grid-cols-6 gap-4 p-6 text-white bg-primary border border-primary rounded-t-lg items-center">
        {/* Logo */}
        <div className="col-span-2 md:col-span-1 flex justify-center md:justify-start">
            <img 
            src={LogoBlanco} 
            alt="Logo AGEMI" 
            className="w-20 md:w-40 h-auto object-contain"
            />
        </div>
        <div className="col-span-4 md:col-span-5 flex flex-col text-sm md:text-base">
          <h1 className="text-3xl font-bold">Departamentos</h1>
        <p className="text-base text-white mt-1 md:mt-2">
          Gestión de los departamentos del sistema
        </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <Select
              value={filtroActivoDepartamento ? '1' : '0'}
              onChange={(e) => setFiltroActivoDepartamento(e.target.value === '1')}
              options={[
                { value: '1', label: 'Departamentos Activos' },
                { value: '0', label: 'Departamentos Inactivos' },
              ]}
              className="w-full md:w-auto bg-white text-primary"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-end mb-4 gap-2">
            {!mostrarFormularioDepartamento && (
              <Button onClick={() => setMostrarFormularioDepartamento(true)} variant="accent" className="w-full md:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Departamento
              </Button>
            )}
          </div>
          {error && (
            <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error al cargar departamentos</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary mr-2" />
              <p>Cargando departamentos...</p>
            </div>
          ) : departamentos.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay departamentos para mostrar</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px] border-gray-400 mb-10">
                <table className="min-w-full text-left">
                  <thead className="bg-primary text-white sticky top-0">
                    <tr>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Nombre</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departamentos.map((departamento) => (
                      <tr key={departamento.idDepartamento} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="px-3 md:px-4 py-2 md:py-3 font-medium text-xs md:text-sm">
                          <div className="truncate">{departamento.departamento}</div>
                        </td>
                        <td className="px-3 md:px-4 py-2 md:py-3">
                          <div className="flex justify-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="accent"
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                setDepartamentoEditando(Number(departamento.idDepartamento));
                                setMostrarFormularioDepartamento(true);
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant={filtroActivoDepartamento ? "destructive" : "primary"}
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                cambiarEstadoDepartamento(Number(departamento.idDepartamento), filtroActivoDepartamento);
                              }}
                            >
                              {filtroActivoDepartamento ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-6 md:grid-cols-6 gap-4 p-6 text-white bg-primary border border-primary rounded-t-lg items-center">
        {/* Logo */}
        <div className="col-span-2 md:col-span-1 flex justify-center md:justify-start">
            <img 
            src={LogoBlanco} 
            alt="Logo AGEMI" 
            className="w-20 md:w-40 h-auto object-contain"
            />
        </div>
        <div className="col-span-4 md:col-span-5 flex flex-col text-sm md:text-base">
          <h1 className="text-3xl font-bold">Municipios</h1>
        <p className="text-base text-white mt-1 md:mt-2">
          Gestión de los municipios del sistema
        </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <Select
              value={filtroActivoMunicipio ? '1' : '0'}
              onChange={(e) => setFiltroActivoMunicipio(e.target.value === '1')}
              options={[
                { value: '1', label: 'Municipios Activos' },
                { value: '0', label: 'Municipios Inactivos' },
              ]}
              className="w-full md:w-auto bg-white text-primary"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-end mb-4 gap-2">
            {!mostrarFormularioMunicipio && (
              <Button onClick={() => setMostrarFormularioMunicipio(true)} variant="accent" className="w-full md:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Municipio
              </Button>
            )}
          </div>
          {error && (
            <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error al cargar municipios</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary mr-2" />
              <p>Cargando municipios...</p>
            </div>
          ) : municipios.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay municipios para mostrar</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px] border-gray-400 mb-10">
                <table className="min-w-full text-left">
                  <thead className="bg-primary text-white sticky top-0">
                    <tr>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Nombre</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Departamento</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {municipios.map((municipio) => (
                      <tr key={municipio.idMunicipio} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="px-3 md:px-4 py-2 md:py-3 font-medium text-xs md:text-sm">
                          <div className="truncate">{municipio.municipio}</div>
                        </td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{municipio.departamento}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3">
                          <div className="flex justify-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="accent"
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                setMunicipioEditando(Number(municipio.idMunicipio));
                                setMostrarFormularioMunicipio(true);
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant={filtroActivoMunicipio ? "destructive" : "primary"}
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                cambiarEstadoMunicipio(Number(municipio.idMunicipio), filtroActivoMunicipio);
                              }}
                            >
                              {filtroActivoMunicipio ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CrearBanco
        isOpen={mostrarFormularioBanco} 
        onClose={async () => {
          setMostrarFormularioBanco(false);
          setBancoEditando(0);
        }}
        onGuardado={async() => {
          await cargarBancos();
          setMostrarFormularioBanco(false);
        }}
        idEditando = {bancoEditando !== null ? Number(bancoEditando) : 0}
      />     

      <CrearDepartamento
        isOpen={mostrarFormularioDepartamento} 
        onClose={async () => {
          setMostrarFormularioDepartamento(false);
          setDepartamentoEditando(0);
        }}
        onGuardado={async() => {
          await cargarDepartamentos();
          setMostrarFormularioDepartamento(false);
        }}
        idEditando = {departamentoEditando !== null ? Number(departamentoEditando) : 0}
      /> 

      <CrearMunicipio
        isOpen={mostrarFormularioMunicipio} 
        onClose={async () => {
          setMostrarFormularioMunicipio(false);
          setMunicipioEditando(0);
        }}
        onGuardado={async() => {
          await cargarMunicipios();
          setMostrarFormularioMunicipio(false);
        }}
        idEditando = {municipioEditando !== null ? Number(municipioEditando) : 0}
      /> 
    </div>
  );
}
